/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-plugin.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAuthorization, useBase64, useTranslation } from 'hooks';
import { OrganisationFields, StudyPluginFields } from 'queries/generated/fragments.generated';
import { GET_STUDY_PLUGIN } from 'queries/studies';
import { GET_ORGANISATION, GET_SYSTEM_PLUGIN, SAVE_PLUGIN } from 'queries/uam';
import Constants from 'utils/constants';
import FieldDefinitionGrid from 'views/components/central-monitoring/field-definition-grid';
import PluginFiltersGrid from 'views/components/central-monitoring/plugin-filters-grid';
import Button from 'views/components/core/buttons/button';
import ExpandableGrid from 'views/components/core/expandable/grid';
import Loading from 'views/components/core/loading';
import Error from 'views/components/error/error';

const AdminPlugin = ({ organisation, study }: { organisation?: OrganisationFields; study?: StudyPluginFields }) => {
    const user = useAuthorization();
    const canEdit = !!user?.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    const inputRefPlugin = useRef<HTMLInputElement | null>(null);
    const base64 = useBase64();
    const { t } = useTranslation();
    const [savePlugin, { loading, error }] = useMutation(SAVE_PLUGIN);
    const [plugin, setPlugin] = useState(study ? study.plugin : organisation ? organisation.plugin : null);
    const [
        loadSystemPlugin,
        { loading: loadingSystemPlugin, error: errorLoadingSystemPlugin, data: systemPluginData },
    ] = useLazyQuery(GET_SYSTEM_PLUGIN);
    const [loadStudyPlugin, { loading: loadingStudyPlugin, error: errorStudyPlugin, data: studyPlugin }] =
        useLazyQuery(GET_STUDY_PLUGIN);

    const [pluginError, setPluginError] = useState<string | null>(null);

    useEffect(() => {
        if (!organisation && !study) loadSystemPlugin();
        else if (study) loadStudyPlugin({ variables: { id: study.id } });
    }, [organisation, study]);

    useEffect(() => {
        if (studyPlugin?.study) setPlugin(studyPlugin.study.plugin);
    }, [studyPlugin]);

    useEffect(() => {
        if (systemPluginData?.systemPlugin) setPlugin(systemPluginData.systemPlugin);
    }, [systemPluginData]);

    if (loadingSystemPlugin || loadingStudyPlugin) return <Loading />;

    if (error || errorStudyPlugin) return <Error error={error || errorStudyPlugin} />;

    if (errorLoadingSystemPlugin) return <Error error={errorLoadingSystemPlugin} />;

    const doSavePlugin = (file: any, pluginData: any) => {
        if (!file?.name && !plugin && !pluginData) return;

        savePlugin({
            variables: {
                studyId: study?.id ?? Constants.ObjectId.Empty,
                name: !pluginData ? '' : file?.name,
                plugin: pluginData,
                system: !organisation && !study,
            },
            refetchQueries: [
                study
                    ? {
                          query: GET_STUDY_PLUGIN,
                          variables: {
                              id: study?.id,
                          },
                      }
                    : organisation
                      ? {
                            query: GET_ORGANISATION,
                            variables: {
                                id: organisation.id,
                            },
                        }
                      : {
                            query: GET_SYSTEM_PLUGIN,
                        },
            ],
        }).then((result) => {
            if (!result || !result.data) setPluginError('Unexpected error trying to load plugin.');
            else if (result.errors)
                setPluginError(`Errors trying to save plugin: ${result.errors.map((e: any) => e.message).join(', ')}`);
            else if (result.data.savePlugin?.errors)
                setPluginError(
                    `Error trying to save plugin: ${result.data.savePlugin.errors
                        .map((e: any) => e.message)
                        .join(', ')}`,
                );
            else {
                setPlugin(result.data.savePlugin);
                setPluginError(null);
            }
        });
    };

    return (
        <>
            <br />
            <h2>
                Upload {organisation ? t('common:Organisation') : study ? t('common:Study') : t('common:System')} Plugin
            </h2>
            <hr />
            <br />

            {loading && <Loading />}
            <input
                ref={inputRefPlugin}
                hidden
                accept=".dll"
                multiple={false}
                id="upload-plugin"
                type="file"
                disabled={!canEdit}
                onChange={(event) => {
                    if (event.target.files && event.target.files.length) {
                        const file = event.target.files[0];
                        if (file.size > 1024 * 1024 * 10) {
                            alert('Max file size is 10MB.  Please shrink your file and try again.');
                            inputRefPlugin.current.value = '';
                            return;
                        }

                        base64.get(file).then((p: any) => doSavePlugin(file, p));
                    }
                    inputRefPlugin.current.value = '';
                }}
            />
            <label htmlFor="upload-plugin">
                <Button id="select-file" component="span" disabled={loading || !canEdit} text="common:selectFile" />
            </label>
            {!!plugin && (
                <Button
                    component="span"
                    disabled={loading || !canEdit}
                    text="admin:removePlugin"
                    style={{ marginLeft: 10 }}
                    onClick={() => doSavePlugin(null, null)}
                />
            )}
            <br />
            {pluginError && <span>Error loading plugin: {pluginError}</span>}
            {!!plugin && (
                <>
                    <br />
                    <hr />
                    <br />
                    <h2>Plugin Details:</h2>
                    <ExpandableGrid
                        customKey="filters"
                        items={[{ ...plugin }]}
                        showPager={false}
                        groupable={false}
                        scrollable="scrollable"
                        reorderable={false}
                        pageable={false}
                        showFirstColumn={false}
                        resizable
                        addScroll
                        options={{
                            allowExcelExport: false,
                            allowExpandCollapseAll: false,
                            useFilterChips: true,
                        }}
                        columns={[
                            {
                                label: 'Name',
                                field: 'name',
                                minWidth: 150,
                                notFilterable: true,
                                notSortable: true,
                            },
                            {
                                label: 'Filename',
                                field: 'fileName',
                                minWidth: 250,
                                notFilterable: true,
                                notSortable: true,
                            },
                            {
                                label: 'Version',
                                field: 'version',
                                minWidth: 120,
                                notFilterable: true,
                                notSortable: true,
                            },
                        ]}
                    />
                    <br />
                    <br />
                    {plugin.fields?.length ? (
                        <>
                            <h2>Fields:</h2>
                            <FieldDefinitionGrid
                                fields={plugin.fields.map((f: any) => ({
                                    ...f,
                                    description: f.description ?? '',
                                    tags: f.tags?.map((tag: any) => t(`cm:${tag}`)).join(', '),
                                }))}
                            />
                        </>
                    ) : (
                        'No Fields.'
                    )}
                    <br />
                    <br />
                    {plugin.filters?.length ? (
                        <>
                            <h2>Filters:</h2>
                            <PluginFiltersGrid
                                filters={plugin.filters.map((f: any) => ({
                                    ...f,
                                    tags: f.tags?.map((tag: any) => t(`cm:${tag}`)).join(', '),
                                }))}
                            />
                        </>
                    ) : (
                        'No Filters'
                    )}
                    <br />
                    <br />
                    <h2>Transformation Rules: {plugin.transformRules ? 'Yes' : 'No'}</h2>
                    {/* -- Row data sources (custom data sources) are not currently being used
                    <br/><br/>
                    {plugin.rowDataSources?.length > 0 && <span><b>Row Data Sources:</b> {plugin.rowDataSources?.join(', ') ?? ''}</span>}
                    {!plugin.rowDataSources?.length && <span>No Row Data Sources.</span>}
                */}
                </>
            )}
        </>
    );
};

export default AdminPlugin;
